import React, { useEffect, useState } from "react"
import { Swiper, SwiperSlide } from "swiper/react"
import { Navigation } from "swiper/modules"
import "../../../src/assets/scss/custom/apptransition.scss"
import { motion } from "framer-motion"
import SEO from "../../components/Common/SEO"
import { PAGE_SEO } from "../../config/seoConfig"
import { generateCanonicalUrl, applySEODate } from "../../utils/seoUtils"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Modal,
  ModalBody,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
  Container,
} from "reactstrap"
import Breadcrumb from "../../components/Common/Breadcrumb"
import axios from "axios"
import {api} from "../../utils/variables"
import HitsCard from "./HitsCard"
import SkeletonParagraph from "../../additional_components/common/SkeletonParagraph"
import RatingCard from "./RatingCard"
import "./Hits.css"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

export default function Hits() {
  const { trackEvent } = useAnalytics()
  const [platform, changePlatform] = useState("all")
  const [hits, setHits] = useState([])
  const [hitsDetail, setHitsDetail] = useState([])
  const [sortedTopGross, setSortedTopGross] = useState([])
  const [isSorted, setIsSorted] = useState(false) // Track sorting state
  const [itemsToShow, setItemsToShow] = useState(6) // Initially show 6 items
  const [showAll, setShowAll] = useState(false)
  const [showTopHits, setShowTopHits] = useState(false) // For Top Hits section
  const [showTrendingCards, setShowTrendingCards] = useState(false) // For Trending cards section
  const [viewMode2, setViewMode2] = useState("topgross") // "selling", "earning", "both"
  const [loading, setLoading] = useState(false)
  const isReleasedMeta = sessionStorage.getItem("releaseMeta")
  const [trendKeywords, setTrendKeyword] = useState([])
  const [trendCategory, setTrendCategory] = useState([])

  const [showMoreKey, setShowMoreKey] = useState(false)
  const [visibleKeywords, setVisibleKeywords] = useState([])

  const [showMoreCat, setShowMoreCat] = useState(false)
  const [visibleCategory, setVisibleCategory] = useState([])

  const [selectedKeyword, setSelectedKeyword] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)

  const handleKeywordClick = (keyword) => {
    setSelectedKeyword(selectedKeyword === keyword ? null : keyword)
  }

  const handleCategoryClick = (category) => {
    setSelectedCategory(selectedCategory === category ? null : category)
  }

  useEffect(() => {
    setVisibleKeywords(showMoreKey ? trendKeywords : trendKeywords.slice(0, 9))
  }, [showMoreKey, trendKeywords])
  
  useEffect(() => {
    setVisibleCategory(showMoreCat ? trendCategory : trendCategory.slice(0, 9))
  }, [showMoreCat, trendCategory])

  useEffect(() => {
    setLoading(true)
    fetchAll()
  }, [])

  const fetchAll = () => {
    try {
      fetchAllTimeHits()
      fetchTrendingKeywords()
      fetchTrendingCategory()
    } finally {
    }
  }

  const fetchAllTimeHits = async () => {
    try {
      const res = await axios.get(`${api}/tracker/dashboard/all-time-hits`)
      setHits(res.data.data)
      setHitsDetail(res.data.details)
      trackEvent(ANALYTICS_EVENTS.FEATURE.CUSTOM_INSIGHT_VIEWED, { insight_type: ANALYTICS_EVENTS.CUSTOM_INSIGHT_TYPES.TOP_KEYWORD_CATEGORIES })
    } catch (error) {
      console.error("Error fetching in releases:", error)
    } finally {
      setLoading(false)
    }
  }
  const fetchTrendingKeywords = async () => {
    try {
      const res = await axios.get(`${api}/tracker/dashboard/trending_keywords`)
      setTrendKeyword(res.data.data)
    } catch (error) {
      console.error("Error fetching in releases:", error)
    } finally {
      //   setLoading(false)
    }
  }
  const fetchTrendingCategory = async () => {
    try {
      const res = await axios.get(
        `${api}/tracker/dashboard/trending_categories`
      )
      setTrendCategory(res.data.data)
    } catch (error) {
      console.error("Error fetching in releases:", error)
    } finally {
      //   setLoading(false)
    }
  }
  const handlePlatformChange = platform => {
    changePlatform(platform) // Update the platform

    const platformReleaseGames =
      platform !== "all"
        ? hits.filter(game => game.platform === platform)
        : hits // If "all", return all games
    setSortedTopGross(platformReleaseGames)
  }

  const handleSort = event => {
    const sortBy = event.target.value
    let sortedRelease = []
    if (sortBy === "revenue") {
      sortedRelease = [...hits].sort((a, b) => b.revenue - a.revenue)
    } else if (sortBy === "downloads") {
      sortedRelease = [...hits].sort((a, b) => b.downloads - a.downloads)
    } else {
      sortedRelease = hits
    }
    // setSortedTrendingApps(sortedData)
    setSortedTopGross(sortedRelease)
    setIsSorted(true)
  }

  useEffect(() => {
    if (showAll) setItemsToShow(hits.length)
    else setItemsToShow(6)
  }, [showAll]) // Re-run when "showAll" changes or data length changes

  const slides = isSorted
    ? sortedTopGross.slice(0, itemsToShow)
    : hits.slice(0, itemsToShow)

  return (
    <React.Fragment>
      <SEO
        title={applySEODate(PAGE_SEO.topKeywordsCategories.title)}
        description={PAGE_SEO.topKeywordsCategories.description}
        keywords={PAGE_SEO.topKeywordsCategories.keywords}
        canonical={generateCanonicalUrl('/top-keywords-categories')}
        robots={PAGE_SEO.topKeywordsCategories.robots}
        pageType="topKeywordsCategories"
      />
      <div className="page-content">
      <div className="hits-main-content">
        <div className="hits-page-content">
          <div className="hits-container">
            <div className="hits-header">
              <div>
                <h1 className="hits-title" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                  Top Hits
                </h1>
                
                <p className="hits-description">
                  Unlock powerful analytics with Custom Insights, your ultimate tool
                  for tracking top app improvements, revenue trends, and key
                  performance metrics. Stay ahead of the competition with real-time
                  data, actionable insights, and in-depth reports tailored for app
                  developers, investors, and businesses.
                </p>
              </div>
              
              <div className="hits-controls">
                <div className="hits-control-group">
                  <label className="hits-control-label">Sort by</label>
                  <select className="form-select hits-select" onChange={handleSort}>
                    <option value="">Select</option>
                    <option value="revenue">Revenue</option>
                    <option value="downloads">Downloads</option>
                  </select>
                </div>
                
                <div className="hits-control-group">
                  <label className="hits-control-label">Date</label>
                  <input
                    className="form-control hits-date-input"
                    type="date"
                    defaultValue={new Date().toISOString().split("T")[0]}
                  />
                </div>
              </div>
            </div>

            {/* App Cards */}
            <div className="w-100">
              {loading ? (
                <div>
                  {[1, 2, 3, 4, 5, 6].map((index) => (
                    <div key={index} className="mb-3">
                      <SkeletonParagraph lines={3} isHighlighted={index === 1} />
                    </div>
                  ))}
                </div>
              ) : (
                <>
                  {/* Show cards based on showAll state */}
                  {slides.slice(0, showTopHits ? hits.length : 2).map((app, index) => (
                    <HitsCard 
                      key={app?.item_id || index}
                      gamedata={app} 
                      details={hitsDetail[`${app?.item_id}`]} 
                    />
                  ))}
                  
                  {/* First See More button */}
                  {!loading && hits.length > 2 && (
                    <div className="text-center mt-4">
                      <Button 
                        color="danger" 
                        className="hits-see-more-button" 
                        onClick={() => setShowTopHits(!showTopHits)}
                      >
                        {showTopHits ? "See Less" : "See More"}
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Trending Section */}
            <div className="hits-trending-section">
              {/* Trending Header */}
              <div className="hits-trending-header">
                <h2 className="hits-title">Trending</h2>
              </div>
              
              <div className="hits-trending-pills-container">
                <h3 className="hits-trending-subtitle">Trending Keywords:</h3>
                <div className="hits-trending-pills-wrapper">
                  {visibleKeywords.map((keyword, index) => (
                    <div 
                      key={index} 
                      className={`hits-trending-pill ${selectedKeyword === keyword ? 'selected' : ''}`}
                      onClick={() => handleKeywordClick(keyword)}
                    >
                      <span className="hits-trending-pill-text">{keyword}</span>
                    </div>
                  ))}
                  {trendKeywords.length > 8 && (
                    <Button
                      color="link"
                      className="hits-more-button"
                      onClick={() => setShowMoreKey(!showMoreKey)}
                    >
                      {showMoreKey ? "Show Less" : "Show More"}
                    </Button>
                  )}
                </div>
              </div>
              
              <div className="hits-trending-pills-container">
                <h3 className="hits-trending-subtitle">Trending Categories:</h3>
                <div className="hits-trending-pills-wrapper">
                  {visibleCategory.map((category, index) => (
                    <div 
                      key={index} 
                      className={`hits-trending-pill ${selectedCategory === category ? 'selected' : ''}`}
                      onClick={() => handleCategoryClick(category)}
                    >
                      <span className="hits-trending-pill-text">{category}</span>
                    </div>
                  ))}
                  {trendCategory.length > 8 && (
                    <Button
                      color="link"
                      className="hits-more-button"
                      onClick={() => setShowMoreCat(!showMoreCat)}
                    >
                      {showMoreCat ? "Show Less" : "Show More"}
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Additional App Cards after trending sections */}
              <div className="mt-4">
                {loading ? (
                  <div>
                    {[1, 2].map((index) => (
                      <div key={`skeleton-${index}`} className="mb-3">
                        <SkeletonParagraph lines={3} isHighlighted={index === 1} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {slides.slice(2, showTrendingCards ? hits.length : 4).map((app, index) => (
                      <HitsCard 
                        key={`trending-${app?.item_id || index}`}
                        gamedata={app} 
                        details={hitsDetail[`${app?.item_id}`]} 
                      />
                    ))}
                    
                    {/* Second See More button */}
                    {!loading && hits.length > 4 && (
                      <div className="text-center mt-4">
                        <Button 
                          color="danger" 
                          className="hits-see-more-button" 
                          onClick={() => setShowTrendingCards(!showTrendingCards)}
                        >
                          {showTrendingCards ? "See Less" : "See More"}
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </React.Fragment>
  )
}
